// apps/webapp/src/screens/talk/message-subscriptions.ts
/**
 * Zone-aware app.aid.message firehose subscriptions for the Talk tab.
 *
 * Every zone gets a background AREA live-tail (drives the picker's unread badges).
 * The selected zone additionally gets a one-shot backward AREA history backfill, and
 * — when greater-region scope is active — a region live-tail + backward region
 * backfill. Delivered events are tagged with their zone id so the app can route them
 * to the right per-zone store and band them relative to that zone.
 *
 * Backward backfill (head → chunked window walking back to seq 0, capped at a target)
 * keeps coarse-cell history bounded while new messages stream live. Lifecycle: pause()
 * closes everything and retains each key's seq cursor, so a later reconcile() resumes
 * the area live-tails from the retained cursor (replaying the gap).
 */
import type { ReceivedEvent } from "@beacon/client-core";
// Browser-safe subpath: the package barrel pulls in the node-only NATS transport (esbuild
// then fails on node builtins — this broke the Docker webapp build once).
import { BackfillResetError } from "@beacon/transport/relay";

interface Sub extends AsyncIterable<ReceivedEvent> {
  readonly cursor: number;
  close(): Promise<void>;
}

type SubClient = {
  subscribe(filter: { cells: string[]; types: string[]; sinceSeq?: number }): Sub;
  head(): Promise<number>;
  backfill(
    filter: { cells: string[]; types: string[] },
    range: { fromSeq: number; toSeq: number },
  ): AsyncIterable<ReceivedEvent>;
};

export interface ZoneSubSpec {
  id: string;
  areaCells: string[];
  regionCells: string[];
  /** Selected zone → run the backward AREA history backfill. */
  selected: boolean;
  /** Selected AND greater-region scope → open the region sub + region backfill. */
  regionActive: boolean;
}

const MESSAGE_TYPES = ["app.aid.message"];
const BACKFILL_TARGET = 200; // ≈ MessageStore.CAP per band
const BACKFILL_CHUNK = 1000; // seq-window per backward step
/** Windows walked back at most per zone (≈ BACKFILL_MAX_CHUNKS × BACKFILL_CHUNK events of
 *  firehose). Unbounded walking over a multi-million-event ephemeral window meant thousands
 *  of sequential requests per zone per session; the purge floor (409) ends the walk earlier. */
const BACKFILL_MAX_CHUNKS = 100;

export interface BackfillProgress { done: number; total: number }

export class MessageSubscriptions {
  #client: SubClient;
  #onMessage: (zoneId: string, evt: ReceivedEvent) => void;
  #active = new Map<string, Sub>();        // key -> open sub
  #zoneOf = new Map<string, string>();     // key -> zoneId (for pump tagging)
  #cellsOf = new Map<string, string>();    // key -> joined cells (detect cell changes)
  #resume = new Map<string, number>();     // key -> retained cursor across pause
  #backfilled = new Set<string>();         // keys whose backfill completed
  #runs = new Map<string, { aborted: boolean }>(); // in-flight backfills by key
  #chunk: number;
  #target: number;
  #onBackfilled?: (zoneId: string) => void;
  #onBackfillProgress?: (zoneId: string, p: BackfillProgress) => void;
  #maxChunks: number;

  constructor(
    client: SubClient,
    onMessage: (zoneId: string, evt: ReceivedEvent) => void,
    opts?: {
      chunk?: number; target?: number; maxChunks?: number;
      onBackfilled?: (zoneId: string) => void;
      /** One call per window scanned: done/total windows (total = the walk budget). */
      onBackfillProgress?: (zoneId: string, p: BackfillProgress) => void;
    },
  ) {
    this.#client = client;
    this.#onMessage = onMessage;
    this.#onBackfilled = opts?.onBackfilled;
    this.#onBackfillProgress = opts?.onBackfillProgress;
    this.#maxChunks = opts?.maxChunks ?? BACKFILL_MAX_CHUNKS;
    this.#chunk = opts?.chunk ?? BACKFILL_CHUNK;
    this.#target = opts?.target ?? BACKFILL_TARGET;
  }

  /** Open/close subs to match `specs` (idempotent; re-subscribes a key whose cells
   *  changed, so a moved live zone or an edited radius takes effect). */
  reconcile(specs: ZoneSubSpec[]): void {
    const want = new Set<string>();
    for (const s of specs) {
      const aKey = `area:${s.id}`;
      const rKey = `region:${s.id}`;
      want.add(aKey);
      this.#ensure(aKey, s.id, s.areaCells); // background area live-tail
      if (s.selected) this.#startBackfill(aKey, s.id, s.areaCells); // area history (once)
      if (s.regionActive) {
        want.add(rKey);
        this.#ensure(rKey, s.id, s.regionCells);
        this.#startBackfill(rKey, s.id, s.regionCells);
      } else if (this.#active.has(rKey)) {
        void this.#close((k) => k === rKey); // demote: drop region for this zone
      }
    }
    const drop = [...this.#active.keys()].filter((k) => !want.has(k));
    if (drop.length) {
      void this.#close((k) => drop.includes(k));
      // A genuinely-removed zone (not a region demote, which is closed above and
      // never enters `drop`) should leave no per-key state behind: its feed store is
      // dropped too, so a later re-add must re-subscribe fresh and re-backfill.
      for (const k of drop) {
        this.#cellsOf.delete(k);
        this.#backfilled.delete(k);
        this.#resume.delete(k);
      }
    }
  }

  /** Open `key` live-tail; if it's already open with DIFFERENT cells, close and
   *  reopen (and clear its completed-backfill flag so the new footprint backfills). */
  #ensure(key: string, zoneId: string, cells: string[]): void {
    const joined = cells.join(",");
    if (this.#active.has(key) && this.#cellsOf.get(key) !== joined) {
      void this.#close((k) => k === key); // synchronously removes key from #active
      this.#backfilled.delete(key);
    }
    this.#cellsOf.set(key, joined);
    this.#open(key, zoneId, cells, undefined);
  }

  /** Close all subs, retaining cursors and aborting backfills (tab leave). */
  async pause(): Promise<void> {
    await this.#close(() => true);
  }

  async #close(match: (key: string) => boolean): Promise<void> {
    for (const [key, run] of this.#runs) if (match(key)) run.aborted = true;
    const closing: Promise<void>[] = [];
    for (const [key, sub] of this.#active) {
      if (!match(key)) continue;
      this.#resume.set(key, sub.cursor);
      closing.push(Promise.resolve(sub.close()).catch(() => {}));
      this.#active.delete(key);
      this.#zoneOf.delete(key);
    }
    await Promise.all(closing);
  }

  #open(key: string, zoneId: string, cells: string[], firstSinceSeq: number | undefined): void {
    if (cells.length === 0 || this.#active.has(key)) return;
    const resumeSeq = this.#resume.get(key);
    const sinceSeq = resumeSeq !== undefined ? resumeSeq : firstSinceSeq;
    const filter = sinceSeq === undefined
      ? { cells, types: MESSAGE_TYPES }
      : { cells, types: MESSAGE_TYPES, sinceSeq };
    const sub = this.#client.subscribe(filter);
    this.#active.set(key, sub);
    this.#zoneOf.set(key, zoneId);
    void this.#pump(key, sub);
  }

  async #pump(key: string, sub: Sub): Promise<void> {
    for await (const evt of sub) {
      const zoneId = this.#zoneOf.get(key);
      if (zoneId !== undefined) this.#onMessage(zoneId, evt);
    }
  }

  /** Manual retry (the Talk tab's Retry button): re-run the history walk for `zoneId`'s open
   *  keys that never completed. A completed key is left alone, so taps cannot flood the relay. */
  retryBackfill(zoneId: string): void {
    for (const key of [`area:${zoneId}`, `region:${zoneId}`]) {
      const cells = this.#cellsOf.get(key);
      if (!cells || !this.#active.has(key) || this.#backfilled.has(key)) continue;
      this.#startBackfill(key, zoneId, cells.split(","));
    }
  }

  #startBackfill(key: string, zoneId: string, cells: string[]): void {
    if (cells.length === 0 || this.#backfilled.has(key)) return;
    const existing = this.#runs.get(key);
    if (existing && !existing.aborted) return;
    const run = { aborted: false };
    this.#runs.set(key, run);
    void this.#runBackfill(key, zoneId, cells, run);
  }

  async #runBackfill(key: string, zoneId: string, cells: string[], run: { aborted: boolean }): Promise<void> {
    const filter = { cells, types: MESSAGE_TYPES };
    try {
      let hi = await this.#client.head();
      let produced = 0;
      let windows = 0;
      while (hi >= 1 && produced < this.#target && windows < this.#maxChunks && !run.aborted) {
        const lo = Math.max(1, hi - this.#chunk + 1);
        try {
          for await (const evt of this.#client.backfill(filter, { fromSeq: lo, toSeq: hi })) {
            if (run.aborted) return;
            this.#onMessage(zoneId, evt);
            if (++produced >= this.#target) break;
          }
        } catch (err) {
          if (err instanceof BackfillResetError) break; // below the purge floor: the history ends here
          throw err;
        }
        windows += 1;
        this.#onBackfillProgress?.(zoneId, { done: windows, total: this.#maxChunks });
        hi = lo - 1;
      }
      if (!run.aborted) {
        this.#backfilled.add(key);
        this.#onBackfilled?.(zoneId); // history for this zone is complete: an empty feed is genuinely empty
      }
    } catch {
      // best-effort history; leave un-backfilled so a later reconcile can retry
    } finally {
      if (this.#runs.get(key) === run) this.#runs.delete(key);
    }
  }
}
